import { z } from "zod";

import * as DbEnum from "../../../generated/db-enum";
import { enumReadSchema } from "../../core/enum-read.schema";

/**
 * The compute a CodeBuild image build runs on — its memory and CPU envelope,
 * which is the ceiling every step of the image build shares. Separate from an
 * app's runtime `resourceQuotas` on purpose: what an image needs to BUILD (a
 * compiler, a bundler, a dependency install) bears no relation to what it needs
 * to SERVE. A build that outgrows it fails with the OOM reason naming the step.
 *
 * - `MEDIUM` — CodeBuild `BUILD_GENERAL1_MEDIUM`: 7 GB, 4 vCPU.
 * - `LARGE`  — CodeBuild `BUILD_GENERAL1_LARGE`: 15 GB, 8 vCPU. The default: the
 *   spike measured it at parity with a warm Nomad build, and medium's cold p50
 *   sat within 5% of the gate.
 *
 * Not a wallclock limit — that is the build budget, and it is the project's. And
 * it bites on CodeBuild only: a Nomad build runs `docker build` through the host
 * daemon, outside any task cgroup, so nothing caps it there.
 *
 * Derived from the Prisma enum rather than restated, so the app's column, the
 * build row's column, the API contract and the agent's wire field are one
 * vocabulary.
 */
export const VIBE_BUILD_COMPUTE_SIZES = DbEnum.VIBE_BUILD_COMPUTE_SIZE_VALUES;

export type VibeBuildComputeSize = (typeof VIBE_BUILD_COMPUTE_SIZES)[number];

export const VibeBuildComputeSizeSchema = z.enum(VIBE_BUILD_COMPUTE_SIZES);

/** `VibeBuildComputeSizeSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeBuildComputeSizeReadSchema = enumReadSchema(VIBE_BUILD_COMPUTE_SIZES);

/**
 * The size an app that has chosen nothing builds on, and the size every
 * CodeBuild build ran on before the setting existed. Exported so no caller
 * writes the literal — the DB column's own default is the same value.
 */
export const VIBE_BUILD_COMPUTE_SIZE_DEFAULT: VibeBuildComputeSize = "LARGE";
